import React, { useState, useEffect, useMemo } from 'react';
import { db, getCollectionName } from './firebase.js';
import { collection, onSnapshot, query, orderBy } from './firestore-compat';
import './App.css';

// Componente de Cronograma Estilo Gantt
function CronogramaProjeto() {
    const [etapas, setEtapas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const currentMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
    const [filtroMes, setFiltroMes] = useState(currentMonth);
    const [filtroResponsavel, setFiltroResponsavel] = useState('todos');
    const [filtroStatus, setFiltroStatus] = useState('todos');

    useEffect(() => {
        const q = query(collection(db, getCollectionName('etapas')), orderBy('order'));
        const unsub = onSnapshot(q, (snapshot) => {
            setEtapas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, []);

    const responsaveisUnicos = useMemo(() => {
        const resp = new Set();
        etapas.flatMap(e => e.tarefas || []).forEach(t => {
            if (t.responsavel) resp.add(t.responsavel);
        });
        return Array.from(resp).sort();
    }, [etapas]);

    // Estatísticas Globais (Independentes dos Filtros)
    const { totalProjeto, concluidasProjeto, pctProjeto } = useMemo(() => {
        const all = etapas.flatMap(e => e.tarefas || []);
        const total = all.length;
        const concluidas = all.filter(t => t.status === 'Feito').length;
        return {
            totalProjeto: total,
            concluidasProjeto: concluidas,
            pctProjeto: total > 0 ? ((concluidas / total) * 100).toFixed(1) : 0
        };
    }, [etapas]);

    // Estatísticas Mensais e Filtradas
    const { totalMes, concluidasMes, pctMes, tarefasFiltradas } = useMemo(() => {
        const all = etapas.flatMap(e => e.tarefas || []);
        
        // KPIs do Mês (Considerando tarefas que terminam no mês, que é o padrão de fluxo)
        let tMes = 0;
        let cMes = 0;
        if (filtroMes !== 'todos') {
            const mesTasks = all.filter(t => t.dataFim && t.dataFim.startsWith(filtroMes));
            tMes = mesTasks.length;
            cMes = mesTasks.filter(t => t.status === 'Feito').length;
        } else {
            tMes = totalProjeto;
            cMes = concluidasProjeto;
        }

        // Filtra tarefas para o Gantt
        const filtradas = all.filter(t => {
            if (!t || !t.dataInicio || !t.dataFim) return false;
            
            const respMatch = filtroResponsavel === 'todos' || t.responsavel === filtroResponsavel;
            const statusMatch = filtroStatus === 'todos' || t.status === filtroStatus;
            const mesMatch = filtroMes === 'todos' || t.dataFim.startsWith(filtroMes) || t.dataInicio.startsWith(filtroMes);
            
            return respMatch && statusMatch && mesMatch;
        }).sort((a, b) => new Date(a.dataInicio) - new Date(b.dataInicio));

        return {
            totalMes: tMes,
            concluidasMes: cMes,
            pctMes: tMes > 0 ? ((cMes / tMes) * 100).toFixed(1) : 0,
            tarefasFiltradas: filtradas
        };
    }, [etapas, filtroResponsavel, filtroStatus, filtroMes, totalProjeto, concluidasProjeto]);


    // Processamento das datas limite do Gantt usando as tarefas filtradas
    const { minDate, maxDate, totalDays } = useMemo(() => {
        if (tarefasFiltradas.length === 0) {
            return { minDate: null, maxDate: null, totalDays: 0 };
        }

        const datasInicio = tarefasFiltradas.map(t => new Date(t.dataInicio + 'T00:00:00'));
        const datasFim = tarefasFiltradas.map(t => new Date(t.dataFim + 'T00:00:00'));

        const min = new Date(Math.min(...datasInicio));
        const max = new Date(Math.max(...datasFim));

        // Respiro de 5 dias
        min.setDate(min.getDate() - 5);
        max.setDate(max.getDate() + 5);

        const diffTime = Math.abs(max - min);
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return { minDate: min, maxDate: max, totalDays: days };
    }, [tarefasFiltradas]);

    // Gerar colunas do Gantt
    const months = useMemo(() => {
        if (!minDate || !maxDate) return [];
        const result = [];
        let current = new Date(minDate);
        current.setDate(1); 

        while (current <= maxDate) {
            const mes = current.toLocaleString('pt-BR', { month: 'short' }).toUpperCase();
            const ano = current.getFullYear();
            const diasNoMes = new Date(ano, current.getMonth() + 1, 0).getDate();
            
            result.push({ label: `${mes}/${ano}`, days: diasNoMes });
            current.setMonth(current.getMonth() + 1);
        }
        return result;
    }, [minDate, maxDate]);

    const getStatusColor = (status, dataFim) => {
        if (status === 'Feito') return '#28a745'; 
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const fim = new Date(dataFim + 'T00:00:00');
        if (fim < hoje) return '#dc3545'; 
        if (status === 'Em andamento') return '#ffc107'; 
        return '#007bff'; 
    };

    if (isLoading) return <div style={{ padding: 40 }}>Carregando cronograma...</div>;

    return (
        <div className="App" style={{ padding: '20px 40px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="page-header" style={{ marginBottom: '10px' }}>
                <h2>Cronograma do Projeto IW</h2>
                <p style={{ color: '#666', marginTop: '5px' }}>Visão temporal e indicadores de performance do projeto.</p>
            </div>

            {/* PAINEL DE KPIs */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
            }}>
                <div style={kpiCardStyle}>
                    <div style={kpiTitleStyle}>Total Projeto</div>
                    <div style={{ ...kpiValueStyle, color: '#007bff' }}>{totalProjeto}</div>
                </div>
                <div style={kpiCardStyle}>
                    <div style={kpiTitleStyle}>Concluídas Projeto</div>
                    <div style={{ ...kpiValueStyle, color: '#28a745' }}>{concluidasProjeto}</div>
                </div>
                <div style={kpiCardStyle}>
                    <div style={kpiTitleStyle}>% Conclusão Projeto</div>
                    <div style={{ ...kpiValueStyle, color: pctProjeto >= 80 ? '#28a745' : '#ffc107' }}>{pctProjeto}%</div>
                </div>
                <div style={{ ...kpiCardStyle, borderLeft: '4px solid #6f42c1' }}>
                    <div style={kpiTitleStyle}>Total do Mês</div>
                    <div style={{ ...kpiValueStyle, color: '#6f42c1' }}>{totalMes}</div>
                </div>
                <div style={{ ...kpiCardStyle, borderLeft: '4px solid #6f42c1' }}>
                    <div style={kpiTitleStyle}>Concluídas no Mês</div>
                    <div style={{ ...kpiValueStyle, color: '#6f42c1' }}>{concluidasMes}</div>
                </div>
                <div style={{ ...kpiCardStyle, borderLeft: '4px solid #6f42c1' }}>
                    <div style={kpiTitleStyle}>% Conclusão do Mês</div>
                    <div style={{ ...kpiValueStyle, color: pctMes >= 80 ? '#28a745' : '#ffc107' }}>{pctMes}%</div>
                </div>
            </div>

            {/* PAINEL DE FILTROS */}
            <div className="filter-bar-container" style={{ marginBottom: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div className="filter-group">
                    <label>Mês / Período:</label>
                    <input 
                        type="month" 
                        value={filtroMes !== 'todos' ? filtroMes : ''} 
                        onChange={(e) => setFiltroMes(e.target.value || 'todos')} 
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                    <button 
                        onClick={() => setFiltroMes('todos')} 
                        style={{ marginLeft: '10px', fontSize: '0.8rem', padding: '4px 8px' }}
                        className="clear-filters-btn"
                    >Ver Todos</button>
                </div>
                <div className="filter-group">
                    <label>Responsável:</label>
                    <select value={filtroResponsavel} onChange={e => setFiltroResponsavel(e.target.value)}>
                        <option value="todos">Todos</option>
                        {responsaveisUnicos.map(r => (
                            <option key={r} value={r}>{r}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Status:</label>
                    <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                        <option value="todos">Todos</option>
                        <option value="Não iniciado">Não iniciado</option>
                        <option value="Em andamento">Em andamento</option>
                        <option value="Feito">Feito</option>
                    </select>
                </div>
            </div>

            {/* GANTT CHART */}
            {tarefasFiltradas.length === 0 ? (
                <div style={{ textAlign: 'center', marginTop: '50px', color: '#666', padding: '40px', backgroundColor: 'white', borderRadius: '8px' }}>
                    <span style={{ fontSize: '3rem' }}>📅</span>
                    <h3>Não há tarefas para os filtros selecionados.</h3>
                </div>
            ) : (
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                    overflowX: 'auto',
                    border: '1px solid #e9ecef',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', backgroundColor: '#f1f3f5' }}>
                        <div style={{ 
                            width: '300px', minWidth: '300px', padding: '15px', fontWeight: 'bold', 
                            color: '#495057', borderRight: '2px solid #dee2e6', display: 'flex', 
                            alignItems: 'center', justifyContent: 'center', backgroundColor: '#e9ecef'
                        }}>Atividades</div>
                        <div style={{ flex: 1, position: 'relative', minWidth: '800px' }}>
                            <div style={{ display: 'flex', height: '100%' }}>
                                {months.map((m, idx) => (
                                    <div key={idx} style={{
                                        flex: m.days, padding: '15px 0', textAlign: 'center',
                                        fontWeight: 'bold', color: '#495057', borderRight: '1px solid #dee2e6',
                                        backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#e9ecef'
                                    }}>{m.label}</div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {tarefasFiltradas.map((tarefa, index) => {
                            const inicio = new Date(tarefa.dataInicio + 'T00:00:00');
                            const fim = new Date(tarefa.dataFim + 'T00:00:00');
                            
                            const diffToStart = Math.abs(inicio - minDate);
                            const daysToStart = Math.ceil(diffToStart / (1000 * 60 * 60 * 24));
                            
                            const diffDuration = Math.abs(fim - inicio);
                            const durationDays = Math.ceil(diffDuration / (1000 * 60 * 60 * 24)) + 1;

                            const leftPercent = (daysToStart / totalDays) * 100;
                            const widthPercent = (durationDays / totalDays) * 100;

                            const barColor = getStatusColor(tarefa.status, tarefa.dataFim);

                            return (
                                <div key={tarefa.id || index} style={{ display: 'flex', borderBottom: '1px solid #f1f3f5' }}>
                                    <div style={{ 
                                        width: '300px', minWidth: '300px', padding: '12px 15px', borderRight: '2px solid #dee2e6',
                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa', display: 'flex',
                                        alignItems: 'center', fontSize: '0.9rem', color: '#444'
                                    }}>
                                        <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={tarefa.text || tarefa.titulo}>
                                            {tarefa.text || tarefa.titulo || 'Tarefa sem título'}
                                        </strong>
                                    </div>
                                    <div style={{ flex: 1, position: 'relative', minWidth: '800px', backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                                        <div style={{ display: 'flex', height: '100%', position: 'absolute', top: 0, left: 0, right: 0, opacity: 0.2 }}>
                                            {months.map((m, idx) => (
                                                <div key={idx} style={{ flex: m.days, borderRight: '1px solid #ccc' }}></div>
                                            ))}
                                        </div>
                                        <div style={{
                                            position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                            left: `${leftPercent}%`, width: `${widthPercent}%`, height: '24px',
                                            backgroundColor: barColor, borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            display: 'flex', alignItems: 'center', padding: '0 10px', color: 'white',
                                            fontSize: '0.75rem', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden',
                                            transition: 'transform 0.2s', cursor: 'pointer'
                                        }}
                                        title={`Responsável: ${tarefa.responsavel}\nStatus: ${tarefa.status}\nPeríodo: ${tarefa.dataInicio} até ${tarefa.dataFim}`}
                                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1.02)'; e.currentTarget.style.filter = 'brightness(1.1)'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; e.currentTarget.style.filter = 'brightness(1)'; }}
                                        >
                                            <span style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.5)' }}>
                                                {tarefa.responsavel ? tarefa.responsavel.split(' ')[0] : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            
            {/* Legenda */}
            <div style={{ marginTop: '20px', display: 'flex', gap: '20px', justifyContent: 'center', fontSize: '0.9rem', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 15, height: 15, backgroundColor: '#007bff', borderRadius: '3px' }}></span> No Prazo / Não Iniciado</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 15, height: 15, backgroundColor: '#ffc107', borderRadius: '3px' }}></span> Em Andamento</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 15, height: 15, backgroundColor: '#28a745', borderRadius: '3px' }}></span> Concluído</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><span style={{ width: 15, height: 15, backgroundColor: '#dc3545', borderRadius: '3px' }}></span> Atrasado</div>
            </div>
        </div>
    );
}

// Estilos dos Cards
const kpiCardStyle = {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #eee'
};

const kpiTitleStyle = {
    fontSize: '0.85rem',
    color: '#666',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: '8px',
    textAlign: 'center'
};

const kpiValueStyle = {
    fontSize: '2rem',
    fontWeight: 'bold'
};

export default CronogramaProjeto;
